/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React from 'react';
import { Text, View } from 'react-native';
import { EmbeddedMetric } from '../../../shared/surface-cards/EmbeddedMetric'
import { EmbeddedMetricGrid } from '../../../shared/surface-cards/EmbeddedMetricGrid';
import { modalExperience } from '../../../shared/modals/modalExperience.styles';
import { DEFAULT_PAYMENT_METHOD_LABELS } from '../../../sales/public-api';
import type { CashVm } from './cashManagement.types';
import { formatMoney, formatSalesCount } from './cashManagement.utils';
import { cashArqueoCopy, cashNonCashTodayLabel } from './cashArqueoCopy';
import { formatDayContextSectionTitle, isTurnoOpenedOnPriorCalendarDay } from './cashCloseTurnoPresentation';
import { cashPanelStyles as panelStyles } from './cashManagement.panelStyles';
import { cashStyles } from './cashManagement.styles';
import { spacing } from '../../../shared/theme/spacing';
import { useContentLayout } from '../../../shared/layout/useContentLayout';

export function CashTurnoSummaryBody({ vm }: { vm: CashVm }) {
  const { layoutWebShell } = useContentLayout('dataWide');
  const mobile = !layoutWebShell;
  const hasSession = Boolean(vm.cashSummary?.activeSession);
  const sessionSalesCount = vm.cashSummary?.sessionSalesCount ?? 0;
  const sessionSalesTotal = vm.cashSummary?.sessionSalesTotal ?? 0;
  const sessionTipsTotal = vm.cashSummary?.sessionTipsTotal ?? 0;
  const sessionPaymentBreakdown = (vm.cashSummary?.sessionPaymentBreakdown ?? []).filter(
    (line) => line.total > 0
  );
  const todaySalesCount = vm.cashSummary?.todaySalesCount ?? 0;
  const todaySalesTotal = vm.cashSummary?.todaySalesTotal ?? 0;
  const todayCashTotal = vm.cashSummary?.todayCashTotal ?? 0;
  const todayDigitalTotal = vm.cashSummary?.todayDigitalTotal ?? 0;
  const todayAllZero =
    todaySalesCount === 0 && todaySalesTotal === 0 && todayCashTotal === 0 && todayDigitalTotal === 0;
  const nonCashLabel = cashNonCashTodayLabel(vm.customerModuleEnabled);
  const openedAt = vm.cashSummary?.activeSession?.openedAt;
  const dayContextTitle = formatDayContextSectionTitle(openedAt);
  const multiDayTurn = openedAt ? isTurnoOpenedOnPriorCalendarDay(openedAt) : false;

  // Caja cerrada sin movimiento hoy → nada que resumir (el form de apertura manda).
  if (!hasSession && todayAllZero) return null;

  if (mobile) {
    return (
      <View style={{ gap: spacing.sm }}>
        {hasSession ? (
          <>
            {/* Metric row — turno (oculta en 0: el hint de abajo ya lo dice) */}
            {sessionSalesCount > 0 || sessionSalesTotal > 0 ? (
              <View style={cashStyles.metricRowMobile}>
                <View style={cashStyles.metricCardMobile}>
                  <Text style={cashStyles.metricLabelMobile}>{cashArqueoCopy.sessionSalesCountLabel}</Text>
                  <Text style={cashStyles.metricValueMobile}>{formatSalesCount(sessionSalesCount)}</Text>
                </View>
                <View style={cashStyles.metricCardMobile}>
                  <Text style={cashStyles.metricLabelMobile}>{cashArqueoCopy.sessionSalesAmountLabel}</Text>
                  <Text style={cashStyles.metricValueMobile}>{formatMoney(sessionSalesTotal)}</Text>
                </View>
              </View>
            ) : null}

            {sessionTipsTotal > 0 ? (
              <View style={cashStyles.metricRowMobile}>
                <View style={[cashStyles.metricCardMobile, { flex: 1 }]}>
                  <Text style={cashStyles.metricLabelMobile}>Propinas del turno</Text>
                  <Text style={cashStyles.metricValueMobile}>{formatMoney(sessionTipsTotal)}</Text>
                </View>
              </View>
            ) : null}

            {/* Payment breakdown */}
            {sessionPaymentBreakdown.length > 0 ? (
              <View style={{ gap: 4 }}>
                <Text style={panelStyles.sectionLabelMobile}>Medios de pago</Text>
                {sessionPaymentBreakdown.map((line) => (
                  <View key={line.methodId} style={cashStyles.paymentLineMobile}>
                    <Text style={cashStyles.paymentMethodMobile}>
                      {DEFAULT_PAYMENT_METHOD_LABELS[line.methodId] ?? line.methodId}
                    </Text>
                    <Text style={cashStyles.paymentAmountMobile}>{formatMoney(line.total)}</Text>
                  </View>
                ))}
              </View>
            ) : sessionSalesCount === 0 ? (
              <Text style={panelStyles.turnoEmptyHint}>Todavía no hay ventas en este turno.</Text>
            ) : null}
          </>
        ) : null}

        {/* Contexto del día / referencia — solo si hubo movimiento */}
        {!todayAllZero ? (
          <>
            {hasSession ? <View style={cashStyles.dividerMobile} /> : null}
            <Text style={panelStyles.sectionLabelMobile}>{dayContextTitle}</Text>
            {multiDayTurn ? (
              <Text style={panelStyles.turnoEmptyHint}>No incluye ventas de días anteriores del turno.</Text>
            ) : null}
            <View style={cashStyles.metricRowMobile}>
              <View style={[cashStyles.metricCardMobile, { flex: 0, flexBasis: '48%' }]}>
                <Text style={cashStyles.metricLabelMobile}>Ventas hoy</Text>
                <Text style={cashStyles.metricValueMobile}>{formatSalesCount(todaySalesCount)}</Text>
              </View>
              <View style={[cashStyles.metricCardMobile, { flex: 0, flexBasis: '48%' }]}>
                <Text style={cashStyles.metricLabelMobile}>Monto hoy</Text>
                <Text style={cashStyles.metricValueMobile}>{formatMoney(todaySalesTotal)}</Text>
              </View>
              <View style={[cashStyles.metricCardMobile, { flex: 0, flexBasis: '48%' }]}>
                <Text style={cashStyles.metricLabelMobile}>Efectivo hoy</Text>
                <Text style={cashStyles.metricValueMobile}>{formatMoney(todayCashTotal)}</Text>
              </View>
              <View style={[cashStyles.metricCardMobile, { flex: 0, flexBasis: '48%' }]}>
                <Text style={cashStyles.metricLabelMobile}>{nonCashLabel}</Text>
                <Text style={cashStyles.metricValueMobile}>{formatMoney(todayDigitalTotal)}</Text>
              </View>
            </View>
          </>
        ) : null}
      </View>
    );
  }

  // ── Wide: original EmbeddedMetricGrid ──
  return (
    <View style={{ gap: spacing.sm }}>
      {hasSession && (sessionSalesCount > 0 || sessionSalesTotal > 0 || sessionTipsTotal > 0) ? (
        <EmbeddedMetricGrid title="Este turno">
          <EmbeddedMetric label={cashArqueoCopy.sessionSalesCountLabel} value={formatSalesCount(sessionSalesCount)} />
          <EmbeddedMetric label={cashArqueoCopy.sessionSalesAmountLabel} value={formatMoney(sessionSalesTotal)} />
          {sessionTipsTotal > 0 ? (
            <EmbeddedMetric label="Propinas del turno" value={formatMoney(sessionTipsTotal)} />
          ) : null}
        </EmbeddedMetricGrid>
      ) : null}

      {hasSession ? (
        sessionPaymentBreakdown.length > 0 ? (
          <View style={{ gap: 4 }}>
            <Text style={panelStyles.sectionLabel}>Medios de pago</Text>
            <EmbeddedMetricGrid gridStyle={{ gap: 12 }}>
              {sessionPaymentBreakdown.map((line) => (
                <EmbeddedMetric
                  key={line.methodId}
                  label={DEFAULT_PAYMENT_METHOD_LABELS[line.methodId] ?? line.methodId}
                  value={formatMoney(line.total)}
                  minWidth={100}
                />
              ))}
            </EmbeddedMetricGrid>
          </View>
        ) : sessionSalesCount === 0 ? (
          <Text style={panelStyles.turnoEmptyHint}>Todavía no hay ventas en este turno.</Text>
        ) : null
      ) : null}

      {!todayAllZero ? (
        <EmbeddedMetricGrid
          title={dayContextTitle}
          hint={multiDayTurn ? 'No incluye ventas de días anteriores del turno.' : undefined}
          dividerTop={hasSession}
        >
          <EmbeddedMetric label="Ventas hoy" value={formatSalesCount(todaySalesCount)} />
          <EmbeddedMetric label="Monto hoy" value={formatMoney(todaySalesTotal)} />
          <EmbeddedMetric label="Efectivo hoy" value={formatMoney(todayCashTotal)} />
          {vm.cashOutsideSession != null && vm.cashOutsideSession > 0 ? (
            <EmbeddedMetric
              label="Efectivo fuera del turno"
              value={formatMoney(vm.cashOutsideSession)}
              minWidth={140}
            />
          ) : null}
          <EmbeddedMetric label={nonCashLabel} value={formatMoney(todayDigitalTotal)} />
        </EmbeddedMetricGrid>
      ) : null}
    </View>
  );
}
